<template>
  <v-form ref="form" v-model="valid" lazy-validation class="action-editor" @submit.prevent="validate">
    <v-row dense>
      <v-col cols="12" lg="5">
        <v-card outlined class="action-editor__card mb-3">
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-gesture-tap-button</v-icon>
            Présentation de l’action
          </v-card-title>
          <v-card-text class="pb-2">
            <v-text-field
              v-model="params.name"
              :rules="requiredRules"
              label="Nom du bouton d’action"
              prepend-inner-icon="mdi-form-textbox"
              outlined
              dense
              required
            />

            <v-text-field
              v-model="params.icon"
              label="Icône MDI"
              placeholder="mdi-pencil"
              prepend-inner-icon="mdi-emoticon-outline"
              outlined
              dense
              clearable
              hint="Exemple : mdi-pencil, mdi-delete ou mdi-eye"
            >
              <template v-if="params.icon" v-slot:append>
                <v-icon>{{ params.icon }}</v-icon>
              </template>
            </v-text-field>

            <v-textarea
              v-model="params.params.ifconfirm"
              label="Texte de confirmation avant l’action"
              prepend-inner-icon="mdi-message-alert-outline"
              outlined
              dense
              rows="2"
              auto-grow
              clearable
            />
          </v-card-text>
        </v-card>

        <v-card outlined class="action-editor__card mb-3">
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-directions-fork</v-icon>
            Comportement
          </v-card-title>
          <v-card-text class="pb-2">
            <v-select
              v-model="params.typelink"
              :items="linkTypes"
              :rules="requiredRules"
              label="Action à effectuer"
              prepend-inner-icon="mdi-cursor-default-click-outline"
              outlined
              dense
              required
            />

            <template v-if="params.typelink === 'api'">
              <v-select
                v-model="params.params.apilink.type"
                :items="apiMethods"
                :rules="requiredRules"
                label="Méthode d’envoi"
                prepend-inner-icon="mdi-api"
                outlined
                dense
                required
              />

              <v-text-field
                v-model="params.params.apilink.url"
                :rules="requiredRules"
                label="URL de l’API"
                prepend-inner-icon="mdi-link-variant"
                outlined
                dense
                required
                persistent-hint
                hint="La variable table permet de cibler les valeurs provenant du tableau."
              />

              <v-switch
                v-model="params.params.head"
                color="primary"
                inset
                class="mt-1 mb-4"
                label="Afficher l’action dans l’en-tête du tableau"
                persistent-hint
                hint="L’action sera visible lorsqu’au moins une ligne est sélectionnée. Utilisez selected à la place de [[table||id]] pour cibler toutes les lignes."
              />
            </template>

            <v-text-field
              v-else-if="params.typelink === 'href'"
              v-model="params.params.apilink.url"
              :rules="requiredRules"
              label="Adresse du lien"
              prepend-inner-icon="mdi-open-in-new"
              outlined
              dense
              required
              persistent-hint
              hint="La variable table permet de cibler les valeurs provenant du tableau."
            />

            <template v-else-if="params.typelink === 'link'">
              <v-autocomplete
                v-model="params.page_name"
                :items="listePages"
                :rules="requiredRules"
                label="Page de destination"
                prepend-inner-icon="mdi-file-link-outline"
                outlined
                dense
                clearable
                required
                @change="onPageChange"
              />

              <v-select
                v-model="params.target"
                :items="linkTargets"
                :rules="requiredRules"
                label="Mode d’ouverture"
                prepend-inner-icon="mdi-open-in-app"
                outlined
                dense
                required
              />
            </template>
          </v-card-text>
        </v-card>

        <v-card
          v-if="params.target === 'modal' && params.typelink === 'link'"
          outlined
          class="action-editor__card mb-3"
        >
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-window-maximize</v-icon>
            Paramètres de la fenêtre
          </v-card-title>
          <v-card-text>
            <param-dialog
              :page="null"
              :params="params.paramdialog"
              @returnParamDialog="changeParamDialog"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="7">
        <v-card v-if="moduleType === 'table'" outlined class="action-editor__card mb-3">
          <v-card-text class="py-2">
            <v-switch v-model="params.params.showInRowMenu" label="Afficher dans le menu d’actions de la ligne" inset hide-details class="mt-0" />
            <div class="text-caption text--secondary mt-1">Décochez pour conserver cette action uniquement sur les colonnes ou les éléments auxquels elle est attribuée.</div>
          </v-card-text>
        </v-card>
        <v-card outlined class="action-editor__card mb-3">
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-code-json</v-icon>
            Paramètres transmis
          </v-card-title>
          <v-card-subtitle class="pb-2">
            Définissez les paramètres d’URL, leur provenance et leur type.
          </v-card-subtitle>
          <v-card-text class="pt-0">
            <p v-if="moduleType === 'table'" class="text-caption mb-2">Pour un élément de collection, choisissez une valeur libre : <code>[[item||id]]</code> ou <code>[[item||typeprod]]</code> lit l’élément cliqué ; <code>[[table||id]]</code> lit la ligne principale. Ces variables sont aussi utilisables dans les conditions.</p>
            <ArrayTable
              :item="{
                otherparams: { arrayTable: urlParams },
                defaultprops: {
                  show: true,
                  tableDraggable: false,
                  tableDel: true,
                  hideFooter: true,
                  hidesearch: true,
                  addTitle: 'Ajouter un paramètre URL'
                }
              }"
              :valField="params.get"
              :change="updateGet"
              @edit="syncUrlValueType"
              @create="syncUrlValueType"
            />
          </v-card-text>
        </v-card>

        <v-card v-if="params.typelink === 'link'" outlined class="action-editor__card mb-3">
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-tab</v-icon>
            Onglets à ouvrir
          </v-card-title>
          <v-card-text>
            <p class="text-caption mb-3">
              Sélectionnez les onglets à ouvrir dans la page de destination. Chaque module ne peut être ciblé qu’une fois.
              Pour un onglet dynamique, les critères identifient l’occurrence : par exemple <code>sites_id</code> avec <code>[[table||site_id]]</code>.
            </p>
            <ArrayTable
              ref="tabNavigation"
              :item="{
                otherparams: { arrayTable: tabNavigationFields },
                defaultprops: { show: true, tableDraggable: false, tableDel: true, hideFooter: true, hidesearch: true, addTitle: 'Ajouter un onglet à ouvrir' }
              }"
              :valField="tabNavigationRows"
              :change="updateTabNavigation"
              @edit="prepareTabNavigationDialog"
              @create="prepareTabNavigationDialog"
            />
            <v-alert v-if="tabNavigationError" dense text type="error" class="mt-3 mb-0">{{ tabNavigationError }}</v-alert>
          </v-card-text>
        </v-card>

        <v-card outlined class="action-editor__card mb-3">
          <v-card-title class="action-editor__section-title">
            <v-icon left color="primary">mdi-filter-cog-outline</v-icon>
            Conditions d’affichage
          </v-card-title>
          <v-card-text>
            <v-alert dense text type="info" class="mb-3">
              Dans une action de ligne, utilisez <code>[[line||mon_AS]]</code> pour cibler une valeur du résultat.
            </v-alert>
            <groupConditions
              :conditions="params.params.conditions"
              @recepVal="updateConditions"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="saveError" dense text type="error">{{ saveError }}</v-alert>
    <div class="action-editor__footer">
      <v-btn type="submit" color="primary" depressed :disabled="!valid">
        <v-icon left>mdi-content-save</v-icon>
        Enregistrer
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { actionEditorLocation } from './navigation'

export default {
  name: 'EditAction',
  props: {
    moduleId: { type: [String, Number], required: true },
    actionId: { type: [String, Number], default: 'new' },
    contextKey: { type: String, default: '' },
    embedded: { type: Boolean, default: false },
    standalone: { type: Boolean, default: false }
  },
  components: {
    ArrayTable: () => import('../../fields/arrayTable.vue'),
    paramDialog: () => import('../outils/paramDialog.vue'),
    groupConditions: () => import('../outils/constructConditions/Conditions.vue')
  },
  data() {
    return {
      urlSend: '',
      valid: true,
      moduleType: null,
      requiredRules: [value => !!value || 'Ce champ ne doit pas être vide'],
      linkTypes: [
        { text: 'Effectuer une action sur l’API', value: 'api' },
        { text: 'Ouvrir une page', value: 'link' },
        { text: 'Ouvrir un lien externe', value: 'href' }
      ],
      apiMethods: [
        { text: 'GET', value: 'get' },
        { text: 'POST', value: 'post' },
        { text: 'PUT', value: 'put' }
      ],
      linkTargets: [
        { text: 'Dans une fenêtre', value: 'modal' },
        { text: 'Dans la page', value: 'center' }
      ],
      params: {
        name: '',
        icon: '',
        typelink: '',
        page_name: '',
        target: '',
        params: { head: false, apilink: { url: '', type: '' }, ifconfirm: '', conditions: [], setactivtab: [] },
        paramdialog: {},
        get: []
      },
      listePages: [],
      tabulationTargets: [],
      tabNavigationRows: [],
      tabNavigationError: '',
      saveError: '',
      tabNavigationFields: [
        { type: 'select', key: 'moduleid', name: 'Module de tabulation', ArrayValue: [], change: this.changeTabModule },
        { type: 'select', key: 'tabid', name: 'Onglet', ArrayValue: [] },
        {
          type: 'arraytable', key: 'params', name: 'Critères de l’onglet dynamique',
          defaultprops: { show: true, tableDraggable: false, tableDel: true, hideFooter: true, hidesearch: true, addTitle: 'Ajouter un critère' },
          otherparams: { arrayTable: [
            { type: '', key: 'key', name: 'Clé du paramètre' },
            { type: '', key: 'value', name: 'Valeur ou variable' }
          ] }
        }
      ],
      currentActionId: this.actionId,
      urlParams: [
        { type: '', key: 'key', name: 'Clé dans l’URL' },
        {
          type: 'select', key: 'type', name: 'Provenance',
          ArrayValue: [
            { text: 'Résultat disponible dans le module', value: 'table' },
            { text: 'Valeur libre', value: 'free' }
          ]
        },
        {
          type: 'select', key: 'typevar', name: 'Type de variable',
          ArrayValue: [
            { text: 'Paramètre', value: 'params' },
            { text: 'Query', value: 'query' }
          ]
        },
        {
          type: 'select', key: 'typedata', name: 'Type de donnée', change: this.changeType,
          ArrayValue: [
            { text: 'Nombre', value: 'number' },
            { text: 'Texte', value: 'text' },
            { text: 'Booléen', value: 'bool' },
            { text: 'Tableau', value: 'array' }
          ]
        },
        {
          type: '', key: 'value', name: 'Valeur',
          defaultprops: {
            show: true,
            tableDraggable: false,
            tableDel: true,
            hideFooter: true,
            hidesearch: true,
            addTitle: 'Ajouter un paramètre'
          },
          otherparams: {
            arrayTable: [
              { type: '', key: 'key', name: 'Clé dans l’URL' },
              {
                type: 'select', key: 'type', name: 'Provenance',
                ArrayValue: [
                  { text: 'Résultat disponible dans le module', value: 'table' },
                  { text: 'Valeur libre', value: 'free' }
                ]
              },
              {
                type: 'select', key: 'typedata', name: 'Type de donnée',
                ArrayValue: [
                  { text: 'Nombre', value: 'number' },
                  { text: 'Texte', value: 'text' }
                ]
              },
              { type: '', key: 'value', name: 'Valeur' }
            ]
          }
        }
      ]
    }
  },
  methods: {
    normalizeAction(action) {
      const loaded = action || {}
      const loadedParams = loaded.params || {}
      return {
        ...this.params,
        ...loaded,
        params: {
          ...this.params.params,
          ...loadedParams,
          apilink: { ...this.params.params.apilink, ...(loadedParams.apilink || {}) },
          conditions: Array.isArray(loadedParams.conditions) ? loadedParams.conditions : [],
          setactivtab: Array.isArray(loadedParams.setactivtab) ? loadedParams.setactivtab : []
        },
        paramdialog: loaded.paramdialog || {},
        get: Array.isArray(loaded.get) ? loaded.get : []
      }
    },
    changeType(item, value) {
      const selectedType = value && typeof value === 'object' ? value.value : value
      this.syncUrlValueType({ typedata: selectedType })
    },
    syncUrlValueType(param = {}) {
      const valueField = this.urlParams.find(element => element.key === 'value')
      if(!valueField) return
      valueField.type = param.typedata === 'array' ? 'arraytable' : ''
      if(param.typedata !== 'array') valueField.name = 'Valeur'
    },
    updateGet(item, value) {
      this.params.get = value
    },
    updateConditions(value) {
      this.params.params.conditions = value
    },
    // Les tableaux éditables restent locaux ; le contrat enregistré utilise un objet de critères.
    loadTabNavigation() {
      this.tabNavigationRows = this.params.params.setactivtab.map(row => ({
        moduleid: Number(row.moduleid),
        tabid: Number(row.tabid),
        params: Object.entries(row.params || {}).map(([key, value]) => ({ key, value }))
      }))
      this.prepareTabNavigationDialog()
      this.tabNavigationError = ''
    },
    prepareTabNavigationDialog(row = {}) {
      const currentModule = Number(row.moduleid)
      this.$set(this.tabNavigationFields[0], 'ArrayValue', this.tabulationTargets.map(module => ({
        text: `${module.name} (${module.id})`,
        value: Number(module.id),
        disabled: Number(module.id) !== currentModule && this.tabNavigationRows.some(target => Number(target.moduleid) === Number(module.id))
      })))
      this.setTabOptions(currentModule)
    },
    setTabOptions(moduleId) {
      const module = this.tabulationTargets.find(target => Number(target.id) === Number(moduleId))
      this.$set(this.tabNavigationFields[1], 'ArrayValue', (module ? module.tabs : []).map(tab => ({
        text: `${tab.name} (${tab.id})${tab.dynamic ? ' — dynamique' : ''}`,
        value: Number(tab.id)
      })))
    },
    changeTabModule(item, value, selectedKey) {
      const moduleId = Number(value && typeof value === 'object' ? value[selectedKey || 'value'] : value)
      const editor = this.$refs.tabNavigation
      if (editor && editor.Create && Number(editor.Create.moduleid) !== moduleId) this.$set(editor.Create, 'tabid', '')
      this.setTabOptions(moduleId)
    },
    updateTabNavigation(item, rows) {
      this.tabNavigationRows = Array.isArray(rows) ? rows : []
      this.syncTabNavigation()
    },
    syncTabNavigation() {
      const modules = new Set()
      const result = []
      this.tabNavigationError = ''
      for (const row of this.tabNavigationRows) {
        const moduleId = Number(row.moduleid)
        const tabId = Number(row.tabid)
        const module = this.tabulationTargets.find(target => Number(target.id) === moduleId)
        if (!Number.isSafeInteger(moduleId) || !moduleId || !Number.isSafeInteger(tabId) || !tabId || !module || !module.tabs.some(tab => Number(tab.id) === tabId)) {
          this.tabNavigationError = 'Sélectionnez un module de tabulation et un onglet appartenant à ce module.'
          return false
        }
        if (modules.has(moduleId)) {
          this.tabNavigationError = 'Un module de tabulation ne peut être ciblé qu’une seule fois.'
          return false
        }
        const criteria = {}
        for (const criterion of (Array.isArray(row.params) ? row.params : [])) {
          const key = String(criterion.key || '').trim()
          if (!key || ['__proto__', 'constructor', 'prototype'].includes(key) || Object.prototype.hasOwnProperty.call(criteria, key)) {
            this.tabNavigationError = 'Chaque critère doit avoir une clé valide et unique dans cet onglet.'
            return false
          }
          criteria[key] = criterion.value === undefined ? '' : criterion.value
        }
        modules.add(moduleId)
        result.push({ moduleid: moduleId, tabid: tabId, params: criteria })
      }
      this.params.params.setactivtab = result
      return true
    },
    onPageChange(value) {
      if(!value) return
      const selectedPage = this.listePages.find(page => page.value === value)
      if(!selectedPage || !selectedPage.url || selectedPage.url.indexOf(':') === -1) return

      selectedPage.url.split('/').forEach(part => {
        if(part.indexOf(':') !== 0) return
        const key = part.replace(':', '')
        if(!this.params.get.find(param => param.key === key)) {
          this.params.get.push({ key, type: 'table', value: key })
        }
      })
    },
    validate() {
      if(!this.$refs.form.validate()) return
      this.saveError = ''
      if(this.params.typelink === 'link' && !this.syncTabNavigation()) return

      this.$store.dispatch('getoverlay', true)
      this.API({ type: 'put', url: this.urlSend, params: this.params }).then(response => {
        if(!response.data || !response.data.id) {
          this.saveError = response.data && response.data.message || 'L’action n’a pas été enregistrée. Vérifiez les onglets sélectionnés.'
          return
        }
        const wasNew = String(this.currentActionId) === 'new'
        this.currentActionId = response.data.id || this.currentActionId
        this.urlSend = `/engine/module/${this.moduleId}/action/${this.currentActionId}/edit`
        this.$emit('saved', { ...this.params, id: this.currentActionId })
        if(wasNew && this.standalone && this.$route.name === 'engine-action-edit') {
          this.$router.replace(actionEditorLocation({ moduleId: this.moduleId, id: this.currentActionId }))
        }
      }).catch(() => { this.saveError = 'L’action n’a pas pu être enregistrée.' })
        .finally(() => this.$store.dispatch('getoverlay', false))
    },
    getActionFromAPI() {
      this.API({ type: 'get', url: this.urlSend, params: {} }).then(response => {
        const { tabulationTargets, moduleType, ...action } = response.data
        this.moduleType = moduleType || null
        if (this.moduleType === 'table') {
          action.params = { ...(action.params || {}), showInRowMenu: action.params?.showInRowMenu !== false }
        }
        this.tabulationTargets = Array.isArray(tabulationTargets) ? tabulationTargets : []
        this.params = this.normalizeAction(action)
        this.loadTabNavigation()
      })
    },
    changeParamDialog(data) {
      this.params.paramdialog = data
    }
  },
  mounted() {
    this.urlSend = `/engine/module/${this.moduleId}/action/${this.currentActionId}/edit`
    this.getActionFromAPI()
    this.listePages = this.$store.getters.listAll.map(page => ({
      text: `${page.displayname} (${page.path})`,
      value: page.name,
      url: page.path
    }))
  }
}
</script>

<style scoped>
.action-editor { width: 100%; }
.action-editor__card {
  overflow: hidden;
  border-color: #e1e5ee;
  border-radius: 10px !important;
}
.action-editor__section-title {
  min-height: 48px;
  padding: 10px 16px;
  border-bottom: 1px solid #edf0f5;
  background: #fafbfe;
  font-size: .98rem;
  font-weight: 600;
}
.action-editor__footer {
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  z-index: 3;
  padding: 10px 4px;
  border-top: 1px solid #e6e9f0;
  background: rgba(255, 255, 255, .96);
  backdrop-filter: blur(5px);
}
code {
  padding: 2px 5px;
  border-radius: 4px;
  background: rgba(63, 81, 181, .08);
  color: #303f9f;
}
@media (max-width: 600px) {
  .action-editor__section-title { padding: 9px 12px; }
  .action-editor__card ::v-deep .v-card__text { padding: 14px 12px; }
  .action-editor__footer .v-btn { width: 100%; }
}
</style>
